// Patch A.1 — Shared Add-to-Calendar helper.
// Serves both confirmed direct-booking success paths (paid + free).
// Do NOT use for enquiry submissions — enquiries are not confirmed appointments.
//
// Exports:
//   • downloadTrialIcs(details)      → .ics blob download (Apple / Outlook desktop / generic)
//   • getGoogleCalendarUrl(details)  → deep link, opens Google Calendar (Android + desktop)
//   • getOutlookCalendarUrl(details) → deep link, opens Outlook.com web calendar
//   • buildIcs(details)              → raw ICS string (exported for tests)

const MELBOURNE_TZ = "Australia/Melbourne";
const EVENT_TITLE = "Mahra Trial Appointment";
const EVENT_LOCATION = "Mahra, Oakleigh South VIC 3167";
const ORGANIZER_EMAIL = "hello@shopmahra.com.au";

function pad(n) {
  return String(n).padStart(2, "0");
}

function _parts({ date, startTime, durationMinutes }) {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = startTime.split(":").map(Number);
  const dtStartLocal = `${y}${pad(m)}${pad(d)}T${pad(hh)}${pad(mm)}00`;
  const endMinutes = hh * 60 + mm + (durationMinutes || 60);
  const endH = Math.floor(endMinutes / 60);
  const endM = endMinutes % 60;
  const dtEndLocal = `${y}${pad(m)}${pad(d)}T${pad(endH)}${pad(endM)}00`;
  // ISO-ish local strings for deep-link providers (they accept naive local times when tz is included).
  const isoStart = `${y}-${pad(m)}-${pad(d)}T${pad(hh)}:${pad(mm)}:00`;
  const isoEnd = `${y}-${pad(m)}-${pad(d)}T${pad(endH)}:${pad(endM)}:00`;
  return { dtStartLocal, dtEndLocal, isoStart, isoEnd };
}

function _description(customerName) {
  return `Your trial appointment with Mahra${customerName ? ` — ${customerName}` : ""}.`;
}

export function buildIcs(details) {
  const { dtStartLocal, dtEndLocal } = _parts(details);
  const now = new Date();
  const dtStamp = `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;
  const uid = `mahra-trial-${details.date}-${(details.startTime || "").replace(":", "")}@shopmahra.com.au`;
  const desc = _description(details.customerName);

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Mahra//Trial Booking//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:${uid}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART;TZID=${MELBOURNE_TZ}:${dtStartLocal}`,
    `DTEND;TZID=${MELBOURNE_TZ}:${dtEndLocal}`,
    `SUMMARY:${EVENT_TITLE}`,
    `DESCRIPTION:${desc}`,
    `LOCATION:${EVENT_LOCATION}`,
    `ORGANIZER;CN=Mahra:mailto:${ORGANIZER_EMAIL}`,
    details.customerEmail
      ? `ATTENDEE;CN=${details.customerName || details.customerEmail};RSVP=TRUE:mailto:${details.customerEmail}`
      : "",
    "STATUS:CONFIRMED",
    "END:VEVENT",
    "END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
}

export function downloadTrialIcs(details) {
  const ics = buildIcs(details);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `mahra-trial-${details.date}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

// Google Calendar deep link.
// Google accepts naive local ISO strings when `ctz` (calendar tz) is supplied.
export function getGoogleCalendarUrl(details) {
  const { isoStart, isoEnd } = _parts(details);
  // Google expects format YYYYMMDDTHHMMSS/YYYYMMDDTHHMMSS for `dates` param.
  const g = (s) => s.replace(/[-:]/g, "");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: EVENT_TITLE,
    dates: `${g(isoStart)}/${g(isoEnd)}`,
    details: _description(details.customerName),
    location: EVENT_LOCATION,
    ctz: MELBOURNE_TZ,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

// Outlook.com web calendar deep link.
export function getOutlookCalendarUrl(details) {
  const { isoStart, isoEnd } = _parts(details);
  const params = new URLSearchParams({
    path: "/calendar/action/compose",
    rru: "addevent",
    subject: EVENT_TITLE,
    startdt: `${isoStart}`,
    enddt: `${isoEnd}`,
    body: _description(details.customerName),
    location: EVENT_LOCATION,
    allday: "false",
  });
  return `https://outlook.live.com/calendar/0/deeplink/compose?${params.toString()}`;
}
